import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { THUMB_FOCUS_GRADIENT } from '@/constants';
import { clamp, normalize } from '@/utils';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import { getDistance } from './BezierControl.helpers';

function Bezier({ id, box, value, animateChanges, onChange }) {
  const [draggingIndex, setDraggingIndex] = React.useState(null);

  const firstHandleRef = React.useRef();
  const secondHandleRef = React.useRef();

  // We need to access value/onChange inside the effect, but if we list it as a dependency, it has to re-register all the event listeners. So we’ll cache them in refs:
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const prefersReducedMotion = usePrefersReducedMotion();

  const { width, height } = box;

  const exceedBoundsBy = 0.05;

  React.useEffect(() => {
    if (typeof draggingIndex !== 'number') {
      return;
    }

    const value = valueRef.current;
    const onChange = onChangeRef.current;

    function handleMouseMove(ev) {
      ev.preventDefault();
      let x, y;

      if (ev.touches) {
        const touch = ev.touches[0];
        [x, y] = [touch.clientX, touch.clientY];
      } else {
        [x, y] = [ev.clientX, ev.clientY];
      }

      // We need to turn the raw mouse data into
      const relativeX = x - box.left;
      const relativeY = y - box.top;

      let normalizedX = normalize(relativeX, 0, width, 0, 1);
      let normalizedY = normalize(relativeY, 0, height, 0, 1);

      normalizedX = clamp(
        normalizedX,
        0 - exceedBoundsBy,
        1 + exceedBoundsBy
      );
      normalizedY = clamp(
        normalizedY,
        0 - exceedBoundsBy,
        1 + exceedBoundsBy
      );

      const newValues = [...value];
      newValues[draggingIndex] = { x: normalizedX, y: normalizedY };

      onChange(newValues);
    }

    function handleMouseUp(ev) {
      setDraggingIndex(null);
      firstHandleRef.current?.blur();
      secondHandleRef.current?.blur();
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [draggingIndex, width, height, box.left, box.top]);

  // Our points will be in "raw" form, ranging from 0 to 1, based
  // on the bottom left corner. We need to transform them to fit
  // in our box.
  const [p1, p2] = value.map((point) => {
    return {
      x: point.x * width,
      y: point.y * height,
    };
  });

  function startDragging(ev, pointIndex) {
    ev.stopPropagation();
    ev.preventDefault();

    setDraggingIndex(pointIndex);
  }

  function handleClickEmptySpace(ev) {
    ev.preventDefault();

    // Figure out which handle we're closest to!
    let x, y;
    if (ev.touches) {
      const touch = ev.touches[0];
      [x, y] = [touch.clientX, touch.clientY];
    } else {
      [x, y] = [ev.clientX, ev.clientY];
    }

    // We need to turn the raw mouse data into
    const relativeX = x - box.left;
    const relativeY = y - box.top;
    const newPoint = {
      x: normalize(relativeX, 0, width, 0, 1),
      y: normalize(relativeY, 0, height, 0, 1),
    };

    const distanceFirstPoint = getDistance(newPoint, value[0]);
    const distanceSecondPoint = getDistance(newPoint, value[1]);

    const relevantPointIndex =
      distanceFirstPoint < distanceSecondPoint ? 0 : 1;

    // Update the handle to this point
    const newValues = [...value];
    newValues[draggingIndex] = newPoint;
    onChange(newValues);

    // Indicate that it's dragging
    setDraggingIndex(relevantPointIndex);
  }

  function handleKeyDown(ev, index) {
    if (!ev.key.match(/^Arrow/)) {
      return;
    }

    ev.preventDefault();
    const valueCopy = [...value];
    const pointCopy = { ...value[index] };

    switch (ev.key) {
      case 'ArrowUp': {
        pointCopy.y = clamp(
          pointCopy.y - 0.05,
          0 - exceedBoundsBy,
          1 + exceedBoundsBy
        );
        valueCopy[index] = pointCopy;
        onChange(valueCopy);
        return;
      }
      case 'ArrowDown': {
        pointCopy.y = clamp(
          pointCopy.y + 0.05,
          0 - exceedBoundsBy,
          1 + exceedBoundsBy
        );
        valueCopy[index] = pointCopy;
        onChange(valueCopy);
        return;
      }
      case 'ArrowLeft': {
        pointCopy.x = clamp(
          pointCopy.x - 0.05,
          0 - exceedBoundsBy,
          1 + exceedBoundsBy
        );
        valueCopy[index] = pointCopy;
        onChange(valueCopy);
        return;
      }
      case 'ArrowRight': {
        pointCopy.x = clamp(
          pointCopy.x + 0.05,
          0 - exceedBoundsBy,
          1 + exceedBoundsBy
        );
        valueCopy[index] = pointCopy;
        onChange(valueCopy);
        return;
      }
    }
  }

  const immediate = prefersReducedMotion || !animateChanges;

  const pathProps = useSpring({
    d: `
    M 0 ${height}
    C ${p1.x} ${p1.y}
      ${p2.x} ${p2.y}
      ${width} 0`,
    immediate,
  });

  const firstHandleButtonProps = useSpring({
    transform: `translate(${p1.x}px, ${p1.y}px)`,
    immediate,
  });
  const firstHandlePathProps = useSpring({
    x2: p1.x,
    y2: p1.y,
    immediate,
  });
  const secondHandleButtonProps = useSpring({
    transform: `translate(${p2.x}px, ${p2.y}px)`,
    immediate,
  });
  const secondHandlePathProps = useSpring({
    x2: p2.x,
    y2: p2.y,
    immediate,
  });

  return (
    <Wrapper>
      <Svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        fill="none"
        onMouseDown={handleClickEmptySpace}
        onTouchStart={handleClickEmptySpace}
      >
        <HandleLine x1={0} y1={height} {...firstHandlePathProps} />
        <HandleLine x1={width} y1={0} {...secondHandlePathProps} />
        <Curve {...pathProps} />
      </Svg>
      <HandleButton
        ref={firstHandleRef}
        as={animated.button}
        id={id}
        onMouseDown={(ev) => startDragging(ev, 0)}
        onTouchStart={(ev) => startDragging(ev, 0)}
        onKeyDown={(ev) => handleKeyDown(ev, 0)}
        data-is-dragging={draggingIndex === 0 ? 'true' : undefined}
        style={firstHandleButtonProps}
      >
        <Handle />
      </HandleButton>
      <HandleButton
        ref={secondHandleRef}
        as={animated.button}
        onMouseDown={(ev) => startDragging(ev, 1)}
        onTouchStart={(ev) => startDragging(ev, 1)}
        onKeyDown={(ev) => handleKeyDown(ev, 1)}
        data-is-dragging={draggingIndex === 1 ? 'true' : undefined}
        style={secondHandleButtonProps}
      >
        <Handle />
      </HandleButton>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Svg = styled.svg`
  display: block;
  overflow: visible;
`;

const Curve = styled(animated.path)`
  stroke: white;
  stroke-width: 5px;
  stroke-linecap: round;
`;

const HandleButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 24px;
  height: 24px;
  cursor: var(--cursor);
  will-change: transform;

  &:focus {
    outline: none;
  }
`;

const Handle = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: white;
  border: 2px solid var(--color-background);
  /* Center the button around the top-left corner of the container */
  transform: translate(-50%, -50%);

  ${HandleButton}:active &,
  ${HandleButton}[data-is-dragging="true"] & {
    background: ${THUMB_FOCUS_GRADIENT};
    cursor: none;
  }

  ${HandleButton}:focus & {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

const HandleLine = styled(animated.line)`
  stroke: var(--color-gray-700);
  stroke-width: 3px;
  stroke-dasharray: 0, 6;
  stroke-linecap: round;
  will-change: transform;
`;

export default Bezier;
